// Tela "Comercial → Composições de preços".
// Entry slim após refactor topológico — UI/lógica em ./comercial-composicoes/.
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Alert, Button, Input, Tag, Table, Typography, Space,
  App as AntApp, Flex, Empty, Radio,
} from 'antd';
import {
  PlusOutlined, SearchOutlined, DeleteOutlined, LinkOutlined, ReloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { API_URL } from '../config';

import type { Composicao, Custo, Proposta } from './comercial-composicoes/types';
import { getToken, fmt, fmtDate } from './comercial-composicoes/utils';
import StatusBadge from './comercial-composicoes/StatusBadge';
import CardMarkupBase from './comercial-composicoes/CardMarkupBase';
import FormNova from './comercial-composicoes/FormNova';
import EditorComposicao from './comercial-composicoes/EditorComposicao';
import { modalOverlay, modalContent } from './comercial-composicoes/utils';
import { useCarga } from './comercial-composicoes/cargaAuxiliar';
import { listarServicos, type ServicoCatalogo } from './comercial-propostas/apiCatalogo';

const { Title, Text } = Typography;

const corpo = (r: any) => (r?.data?.body ? JSON.parse(r.data.body) : r?.data);
// Lista que não chega como lista (ex.: corpo de erro com status 200) é falha, não "vazio".
function listaDe<T>(r: any): T[] {
  const d = corpo(r);
  if (!Array.isArray(d)) throw new Error('resposta inesperada');
  return d;
}
// Markup base da empresa (ADM 12m + impostos + lucro). Resposta sem os impostos
// não é "sem markup": é resposta que não serve, e conta como falha.
async function buscarMarkupBase(): Promise<any> {
  const token = await getToken();
  const d = corpo(await axios.get(`${API_URL}/markup-base`, { headers: { Authorization: token } }));
  if (!d || typeof d !== 'object' || !d.impostos) throw new Error('resposta inesperada');
  return d;
}

export default function ComercialComposicoes() {
  const [comps, setComps] = useState<Composicao[]>([]);
  const [custos, setCustos] = useState<Custo[]>([]);
  const [propostas, setPropostas] = useState<Proposta[]>([]);
  // Serviços e markup base têm estado próprio: a falha de um deles não derruba a
  // tela, mas também NÃO vira "lista vazia" nem "sem markup".
  const servicosCat = useCarga<ServicoCatalogo[]>(listarServicos, []);
  const markup = useCarga<any | null>(buscarMarkupBase, null);
  const markupBase = markup.dado;
  // 'desatualizada' = a recarga falhou, mas a lista de antes continua na tela.
  const [estadoLista, setEstadoLista] = useState<'ok' | 'erro' | 'desatualizada'>('ok');
  const jaCarregou = useRef(false);
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState<'todas' | 'rascunho' | 'ativa' | 'arquivada' | 'templates'>('todas');
  const [busca, setBusca] = useState('');
  const [editando, setEditando] = useState<Composicao | null>(null);
  const [mostrarNovo, setMostrarNovo] = useState(false);

  // Intent vinda de outra página (ex.: botão "Criar composição" no Pipeline).
  // Lida no primeiro carregamento; abre o modal "Nova composição" com proposta pré-vinculada.
  const [intentInicial, setIntentInicial] = useState<{ proposta_id: number; proposta_titulo: string } | null>(null);

  useEffect(() => {
    const raw = sessionStorage.getItem('nova_composicao_intent');
    if (raw) {
      try {
        const intent = JSON.parse(raw);
        if (intent?.proposta_id) setIntentInicial(intent);
      } catch {}
      sessionStorage.removeItem('nova_composicao_intent');
    }
    carregar();
  }, []);

  // Quando os dados terminam de carregar, abre o modal pré-preenchido se houver intent
  useEffect(() => {
    if (!loading && intentInicial && !mostrarNovo && !editando) {
      setMostrarNovo(true);
    }
  }, [loading, intentInicial, mostrarNovo, editando]);

  // Recarrega custos/propostas quando a janela volta a ficar visível
  // (pega alterações feitas em outra aba/janela do sistema).
  useEffect(() => {
    function onFocus() { recarregarAux(); }
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  async function carregar() {
    setLoading(true);
    // As duas cargas auxiliares nunca rejeitam — o resultado fica no estado de cada uma.
    const auxiliares = Promise.all([servicosCat.carregar(), markup.carregar()]);
    try {
      const token = await getToken();
      const [cRes, uRes, pRes] = await Promise.all([
        axios.get(`${API_URL}/comercial/composicoes`, { headers: { Authorization: token } }),
        axios.get(`${API_URL}/comercial/custos`, { headers: { Authorization: token } }),
        axios.get(`${API_URL}/comercial/propostas`, { headers: { Authorization: token } }),
      ]);
      const [cs, us, ps] = [listaDe<Composicao>(cRes), listaDe<Custo>(uRes), listaDe<Proposta>(pRes)];
      setComps(cs); setCustos(us); setPropostas(ps);
      jaCarregou.current = true;
      setEstadoLista('ok');
    } catch {
      // Falha NÃO vira "Nenhuma composição encontrada". Se a lista já tinha vindo,
      // ela continua na tela (a ação que chamou a recarga já foi gravada).
      setEstadoLista(jaCarregou.current ? 'desatualizada' : 'erro');
    } finally {
      await auxiliares;
      setLoading(false);
    }
  }

  // Recarrega só custos e propostas (mais leve, sem spinner)
  async function recarregarAux() {
    try {
      const token = await getToken();
      const [uRes, pRes] = await Promise.all([
        axios.get(`${API_URL}/comercial/custos`, { headers: { Authorization: token } }),
        axios.get(`${API_URL}/comercial/propostas`, { headers: { Authorization: token } }),
      ]);
      setCustos(uRes.data.body ? JSON.parse(uRes.data.body) : uRes.data);
      setPropostas(pRes.data.body ? JSON.parse(pRes.data.body) : pRes.data);
    } catch {}
  }

  async function abrirNovo() {
    await recarregarAux();
    setMostrarNovo(true);
  }
  async function abrirEditor(c: Composicao) {
    await recarregarAux();
    setEditando(c);
  }

  async function excluir(id: string) {
    if (!window.confirm('Excluir definitivamente esta composição?')) return;
    try {
      const token = await getToken();
      await axios.delete(`${API_URL}/comercial/composicoes/${id}`, { headers: { Authorization: token } });
      await carregar();
    } catch (e: any) { alert('Erro: ' + e.message); }
  }

  // Navega para a proposta vinculada (Pipeline) a partir da lista
  function irParaProposta(propId: string) {
    sessionStorage.setItem('pipeline_destacar_proposta_id', propId);
    window.dispatchEvent(new CustomEvent('app-navigate', {
      detail: { modulo: 'comercial', pagina: 'com-pipeline' },
    }));
  }

  // Clona um template em uma nova composição (não-template) e abre o editor.
  async function clonarComposicao(template: Composicao) {
    const novoNome = window.prompt('Nome da nova composição:', `${template.nome.replace(/ \(template\)$/i, '')} — `);
    if (!novoNome) return;
    try {
      const token = await getToken();
      const res = await axios.post(`${API_URL}/comercial/composicoes/${template.id}/clonar`,
        { nome: novoNome, is_template: false },
        { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      if (!data || !data.id) { alert('Erro: resposta inválida do servidor.'); return; }
      // Abre o editor da nova composição
      setEditando({
        id: data.id,
        nome: data.nome,
        descricao: data.descricao ?? null,
        proposta_id: data.proposta_id ?? null,
        proposta_titulo: null,
        markup_padrao_pct: String(data.markup_padrao_pct ?? '30'),
        status: data.status || 'rascunho',
        is_template: !!data.is_template,
        dias_campo: data.dias_campo ?? null,
        markup_config: data.markup_config ?? null,
        observacoes: data.observacoes ?? null,
        criado_em: data.criado_em,
        atualizado_em: data.atualizado_em,
        total_custo: '0', total_venda: '0', n_itens: 0,
      });
      carregar();
    } catch (e: any) {
      alert('Erro ao clonar: ' + (e.response?.data?.error || e.message));
    }
  }

  const filtradas = comps.filter(c => {
    // Templates só aparecem na aba "Templates" (não poluem outros filtros)
    if (filtroStatus === 'templates') {
      if (!c.is_template) return false;
    } else {
      if (c.is_template) return false;
      if (filtroStatus !== 'todas' && c.status !== filtroStatus) return false;
    }
    if (!busca) return true;
    const q = busca.toLowerCase();
    return c.nome.toLowerCase().includes(q)
            || (c.descricao || '').toLowerCase().includes(q)
            || (c.proposta_titulo || '').toLowerCase().includes(q);
  });
  const templatesDisponiveis = comps.filter(c => c.is_template);

  const cols: ColumnsType<Composicao> = [
    {
      title: 'Nome', dataIndex: 'nome', key: 'n',
      width: 380, ellipsis: { showTitle: false },
      render: (_, c) => (
        <Space size={6} style={{ width: '100%' }}>
          {c.is_template && <Tag color="gold" style={{ flexShrink: 0 }}>TEMPLATE</Tag>}
          <Button type="link" size="small"
            style={{ padding: 0, height: 'auto', color: '#0930D0', fontWeight: 500,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              maxWidth: '100%', textAlign: 'left' }}
            title={c.nome}
            onClick={() => abrirEditor(c)}>
            {c.nome}
          </Button>
        </Space>
      ),
    },
    {
      title: 'Proposta vinculada', dataIndex: 'proposta_titulo', key: 'pt',
      width: 260, ellipsis: { showTitle: true },
      render: (v) => v
        ? <Text style={{ fontSize: 12 }}>{v}</Text>
        : <Text type="secondary" italic>—</Text>,
    },
    { title: 'Itens', dataIndex: 'n_itens', key: 'ni', align: 'center', width: 80 },
    {
      title: 'Custo total', dataIndex: 'total_custo', key: 'tc', align: 'right', width: 120,
      render: (v) => fmt(parseFloat(v)),
    },
    {
      title: 'Venda total', dataIndex: 'total_venda', key: 'tv', align: 'right', width: 120,
      render: (v) => <Text strong>{fmt(parseFloat(v))}</Text>,
    },
    {
      title: 'Markup', key: 'mk', align: 'right', width: 80,
      render: (_, c) => {
        const cst = parseFloat(c.total_custo); const vnd = parseFloat(c.total_venda);
        const mk = cst > 0 ? ((vnd / cst) - 1) * 100 : 0;
        return <Text style={{ color: '#0a7f4a' }}>{mk.toFixed(1)}%</Text>;
      },
    },
    {
      title: 'Status', dataIndex: 'status', key: 's', align: 'center', width: 100,
      render: (s) => <StatusBadge status={s} />,
    },
    {
      title: 'Atualizada', dataIndex: 'atualizado_em', key: 'at', align: 'center', width: 100,
      render: (v) => <Text type="secondary" style={{ fontSize: 11 }}>{fmtDate(v)}</Text>,
    },
    {
      title: '', key: 'ac', width: 160, align: 'center',
      render: (_, c) => (
        <Space size={2}>
          {c.is_template && (
            <Button size="small" style={{ color: '#0a7f4a', borderColor: '#0a7f4a' }}
              onClick={() => clonarComposicao(c)} title="Usar como base">Usar</Button>
          )}
          {c.proposta_id && (
            <Button size="small" icon={<LinkOutlined />} onClick={() => irParaProposta(String(c.proposta_id))}
              title={`Abrir proposta: ${c.proposta_titulo || ''}`} />
          )}
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => excluir(c.id)} title="Excluir" />
        </Space>
      ),
    },
  ];

  return (
    <AntApp>
    <div style={{ maxWidth: 1300, margin: '16px auto', padding: '0 24px' }}>
      <Flex align="center" gap={12} wrap="wrap" style={{ marginBottom: 14 }}>
        <Title level={4} style={{ margin: 0, color: '#020D7F' }}>Composições de Preços</Title>
        <Radio.Group value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)} size="small">
          <Radio.Button value="todas">Todas</Radio.Button>
          <Radio.Button value="rascunho">Rascunho</Radio.Button>
          <Radio.Button value="ativa">Ativa</Radio.Button>
          <Radio.Button value="arquivada">Arquivada</Radio.Button>
          <Radio.Button value="templates">📋 Templates</Radio.Button>
        </Radio.Group>
        <Input placeholder="Buscar..." value={busca} onChange={e => setBusca(e.target.value)}
          prefix={<SearchOutlined style={{ color: '#aaa' }} />} size="small" allowClear
          style={{ flex: 1, maxWidth: 300 }} />
        <Button type="primary" icon={<PlusOutlined />} style={{ marginLeft: 'auto' }} onClick={abrirNovo}>
          Nova composição
        </Button>
      </Flex>

      {markupBase && <CardMarkupBase data={markupBase} />}
      {markup.estado === 'erro' && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          title="Não consegui carregar o markup base da empresa."
          description="Isto não quer dizer que não há markup — só que ele não veio. Até ele chegar, composição nova abre com percentuais padrão do sistema, não com os da empresa."
          action={<Button size="small" icon={<ReloadOutlined />} onClick={() => markup.carregar()}>Tentar de novo</Button>} />
      )}

      {estadoLista === 'erro' ? (
        <Alert type="error" showIcon title="Não consegui carregar as composições."
          description="Isto não quer dizer que não há composição — só que a lista não veio. Se o sistema acabou de ser atualizado, espere um minuto e tente de novo."
          action={<Button size="small" icon={<ReloadOutlined />} loading={loading} onClick={carregar}>Tentar de novo</Button>} />
      ) : (
        <>
          {estadoLista === 'desatualizada' && (
            <Alert type="warning" showIcon style={{ marginBottom: 12 }}
              title="Não consegui atualizar a lista. O que você vê pode estar desatualizado."
              action={<Button size="small" icon={<ReloadOutlined />} loading={loading} onClick={carregar}>Tentar de novo</Button>} />
          )}
          <Table rowKey="id" columns={cols} dataSource={filtradas} loading={loading}
            size="small" pagination={{ pageSize: 50, showSizeChanger: true }}
            scroll={{ x: 1400 }}
            locale={{ emptyText: <Empty description="Nenhuma composição encontrada." /> }} />
        </>
      )}

      {mostrarNovo && (
        <FormNova propostas={propostas} templates={templatesDisponiveis}
          markupBase={markupBase} markupBaseFalhou={markup.estado === 'erro'}
          propostaInicialId={intentInicial?.proposta_id}
          onClose={() => { setMostrarNovo(false); setIntentInicial(null); }}
          onCriada={async (nova) => {
            setMostrarNovo(false);
            setIntentInicial(null);
            // Abre o editor da recém-criada imediatamente (sem depender do state atualizado).
            // O editor busca os itens por conta própria via GET /composicoes/{id}.
            setEditando({
              id: nova.id,
              nome: nova.nome,
              descricao: nova.descricao ?? null,
              proposta_id: nova.proposta_id ?? null,
              proposta_titulo: nova.proposta_titulo ?? null,
              markup_padrao_pct: String(nova.markup_padrao_pct ?? '30'),
              status: nova.status || 'rascunho',
              is_template: !!nova.is_template,
              dias_campo: nova.dias_campo ?? null,
              markup_config: nova.markup_config ?? null,
              observacoes: nova.observacoes ?? null,
              criado_em: nova.criado_em,
              atualizado_em: nova.atualizado_em,
              total_custo: '0', total_venda: '0', n_itens: 0,
            });
            carregar(); // recarrega lista em background
          }} />
      )}
      {editando && (
        <EditorComposicao key={editando.id} comp={editando} custos={custos} propostas={propostas}
          servicos={servicosCat.dado} estadoServicos={servicosCat.estado} onRecarregarServicos={servicosCat.carregar}
          markupBase={markupBase} markupBaseFalhou={markup.estado === 'erro'} onRecarregarMarkupBase={markup.carregar}
          onClose={() => setEditando(null)}
          onSaved={async () => { await carregar(); setEditando(null); }}
        />
      )}
    </div>
    </AntApp>
  );
}

// Wrapper auto-carregável: busca composição + dependências por ID.
// Use em outras páginas (ex.: NegocioDetalhe) para abrir o editor sem navegar.
export function EditorComposicaoInline({ composicaoId, origemLabel, onClose, onSaved }: {
  composicaoId: string;
  origemLabel?: string | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [comp, setComp] = useState<Composicao | null>(null);
  const [custos, setCustos] = useState<Custo[]>([]);
  const [propostas, setPropostas] = useState<Proposta[]>([]);
  const servicosCat = useCarga<ServicoCatalogo[]>(listarServicos, []);
  const markup = useCarga<any | null>(buscarMarkupBase, null);
  const { carregar: carregarServicos } = servicosCat;
  const { carregar: carregarMarkup } = markup;
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        // Serviços e markup base entram na espera (o editor lê o markup ao abrir),
        // mas a falha deles fica no estado de cada um — não vira lista vazia.
        const [cRes, custosRes, propsRes] = await Promise.all([
          axios.get(`${API_URL}/comercial/composicoes/${composicaoId}`, { headers: { Authorization: token } }),
          axios.get(`${API_URL}/comercial/custos`, { headers: { Authorization: token } }),
          axios.get(`${API_URL}/comercial/propostas`, { headers: { Authorization: token } }),
          carregarServicos(),
          carregarMarkup(),
        ]);
        setCustos(listaDe<Custo>(custosRes));
        setPropostas(listaDe<Proposta>(propsRes));
        setComp(corpo(cRes));
      } catch (e: any) {
        setErro(e.message || 'Erro ao carregar composição');
      }
    })();
  }, [composicaoId, carregarServicos, carregarMarkup]);

  if (erro) {
    return (
      <div style={modalOverlay} onClick={onClose}>
        <div style={{ ...modalContent, maxWidth: 480 }} onClick={e => e.stopPropagation()}>
          <h3 style={{ margin: '0 0 10px', color: '#B91626' }}>Erro</h3>
          <div style={{ fontSize: 13, color: '#555' }}>{erro}</div>
          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <button onClick={onClose} style={{ padding: '8px 18px', fontSize: 13, border: '0.5px solid #ddd', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>Fechar</button>
          </div>
        </div>
      </div>
    );
  }
  if (!comp) {
    return (
      <div style={modalOverlay} onClick={onClose}>
        <div style={{ ...modalContent, maxWidth: 480 }} onClick={e => e.stopPropagation()}>
          <div style={{ padding: 30, textAlign: 'center', color: '#888' }}>Carregando composição...</div>
        </div>
      </div>
    );
  }
  return (
    <EditorComposicao
      key={comp.id}
      comp={comp} custos={custos} propostas={propostas}
      servicos={servicosCat.dado} estadoServicos={servicosCat.estado} onRecarregarServicos={servicosCat.carregar}
      markupBase={markup.dado} markupBaseFalhou={markup.estado === 'erro'} onRecarregarMarkupBase={markup.carregar}
      origemLabel={origemLabel}
      onClose={onClose} onSaved={onSaved}
    />
  );
}
